import { BlockerComponentProps, BlockerData, BlockerType } from '@cash-web/shared-flow-data-access-context'

import { BlockerStartTotpEnrollment } from './BlockerStartTotpEnrollment/BlockerStartTotpEnrollment'
import { BlockerTotpRecoveryCodes } from './BlockerTotpRecoveryCodes/BlockerTotpRecoveryCodes'
import { BlockerAddress } from './CustomBlockers/BlockerAddress/BlockerAddress'
import { BlockerAlias } from './CustomBlockers/BlockerAlias/BlockerAlias'
import { BlockerAmount } from './CustomBlockers/BlockerAmount/BlockerAmount'
import { BlockerAtmPickerAmount } from './CustomBlockers/BlockerAtmPickerAmount/BlockerAtmPickerAmount'
import { BlockerAuthorizeDigitalWalletPayment } from './CustomBlockers/BlockerAuthorizeDigitalWalletPayment/BlockerAuthorizeDigitalWalletPayment'
import { BlockerCard } from './CustomBlockers/BlockerCard/BlockerCard'
import { BlockerCardCustomization } from './CustomBlockers/BlockerCardCustomization/BlockerCardCustomization'
import { BlockerCardTagOrderConfirmation } from './CustomBlockers/BlockerCardTagOrderConfirmation/BlockerCardTagOrderConfirmation'
import { BlockerCashtag } from './CustomBlockers/BlockerCashtag/BlockerCashtag'
import { BlockerConfirm } from './CustomBlockers/BlockerConfirm/BlockerConfirm'
import { BlockerDisclosure } from './CustomBlockers/BlockerDisclosure/BlockerDisclosure'
import { BlockerEmail } from './CustomBlockers/BlockerEmail/BlockerEmail'
import { BlockerEmailVerification } from './CustomBlockers/BlockerEmailVerification/BlockerEmailVerification'
import { BlockerIdentityVerification } from './CustomBlockers/BlockerIdentityVerification/BlockerIdentityVerification'
import { BlockerInstrumentSelection } from './CustomBlockers/BlockerInstrumentSelection/BlockerInstrumentSelection'
import { BlockerInstrumentSelectionV3 } from './CustomBlockers/BlockerInstrumentSelectionV3/BlockerInstrumentSelectionV3'
import { BlockerInstrumentVerification } from './CustomBlockers/BlockerInstrumentVerification/BlockerInstrumentVerification'
import { BlockerInvestmentEntitySelection } from './CustomBlockers/BlockerInvestmentEntitySelection/BlockerInvestmentEntitySelection'
import { BlockerMultiAllocation } from './CustomBlockers/BlockerMultiAllocation/BlockerMultiAllocation'
import { BlockerName } from './CustomBlockers/BlockerName/BlockerName'
import { BlockerOnboardingInternalRoute } from './CustomBlockers/BlockerOnboardingInternalRoute/BlockerOnboardingInternalRoute'
import { BlockerPasscodeCreation } from './CustomBlockers/BlockerPasscodeCreation/BlockerPasscodeCreation'
import { BlockerPasscodeVerification } from './CustomBlockers/BlockerPasscodeVerification/BlockerPasscodeVerification'
import { BlockerPasskeysUpsell } from './CustomBlockers/BlockerPasskeysUpsell/BlockerPasskeysUpsell'
import { BlockerPasswordCreation } from './CustomBlockers/BlockerPasswordCreation/BlockerPasswordCreation'
import { BlockerPasswordVerification } from './CustomBlockers/BlockerPasswordVerification/BlockerPasswordVerification'
import { BlockerPaymentDeviceCustomization } from './CustomBlockers/BlockerPaymentDeviceCustomization/BlockerPaymentDeviceCustomization'
import { BlockerPayWithCashAuthorization } from './CustomBlockers/BlockerPayWithCashAuthorization/BlockerPayWithCashAuthorization'
import { BlockerPhoneNumber } from './CustomBlockers/BlockerPhoneNumber/BlockerPhoneNumber'
import { BlockerPhoneVerification } from './CustomBlockers/BlockerPhoneVerification/BlockerPhoneVerification'
import { BlockerRewardCode } from './CustomBlockers/BlockerRewardCode/BlockerRewardCode'
import { BlockerSelection } from './CustomBlockers/BlockerSelection/BlockerSelection'
import { BlockerSetAllocationAmount } from './CustomBlockers/BlockerSetAllocationAmount/BlockerSetAllocationAmount'
import { BlockerSignature } from './CustomBlockers/BlockerSignature/BlockerSignature'
import { BlockerStatusInterstitial } from './CustomBlockers/BlockerStatusInterstitial/BlockerStatusInterstitial'
import { BlockerTagConfirmation } from './CustomBlockers/BlockerTagConfirmation/BlockerTagConfirmation'
import { BlockerTaxWebViewBlocker } from './CustomBlockers/BlockerTaxWebViewBlocker/BlockerTaxWebViewBlocker'
import { BlockerTransferOptionSelection } from './CustomBlockers/BlockerTransferOptionSelection/BlockerTransferOptionSelection'
import { RoundUpOnboardingBlocker } from './CustomBlockers/RoundUpOnboardingBlocker/RoundUpOnboardingBlocker'
import { FormBlocker } from './FormBlocker/FormBlocker'

export type RegisteredBlockerComponents = {
  [key in BlockerType]?: React.FC<BlockerComponentProps<key>>
}
// this is a pattern that we should be deprecate, we shouldn't use this unless absolutely necessary
export type RegisteredCustomBlockerComponents = { [key: string]: React.FC<BlockerComponentProps<'form'>> }

export type BlockerComponentOverrides = Partial<RegisteredBlockerComponents>

export type RegistryLookupEntry<Type extends BlockerType> = {
  id?: string
  type: Type
  blocker: BlockerData<Type>
}
export type RegistryLookup = {
  [Type in BlockerType]: RegistryLookupEntry<Type>
}[BlockerType]

// this function exists to avoid circular dependencies by lazy referencing the components
function lookupBlocker(name: BlockerType) {
  /* eslint sort-keys: "warn" */
  const blockerComponentRegistry: RegisteredBlockerComponents = {
    address: BlockerAddress,
    alias_blocker: BlockerAlias,
    amount_blocker: BlockerAmount,
    atm_picker_amount_blocker: BlockerAtmPickerAmount,
    authorize_digital_wallet_payment_blocker: BlockerAuthorizeDigitalWalletPayment,
    card: BlockerCard,
    card_customization: BlockerCardCustomization,
    card_tag_order_confirmation_blocker: BlockerCardTagOrderConfirmation,
    cashtag: BlockerCashtag,
    confirm: BlockerConfirm,
    disclosure: BlockerDisclosure,
    email: BlockerEmail,
    email_verification: BlockerEmailVerification,
    form: FormBlocker,
    identity_verification: BlockerIdentityVerification,
    instrument_selection_blocker: BlockerInstrumentSelection,
    instrument_selection_blocker_v3: BlockerInstrumentSelectionV3,
    instrument_verification: BlockerInstrumentVerification,
    investment_entity_selection_blocker: BlockerInvestmentEntitySelection,
    multi_allocation_blocker: BlockerMultiAllocation,
    name: BlockerName,
    onboarding_internal_route_blocker: BlockerOnboardingInternalRoute,
    passcode_creation: BlockerPasscodeCreation,
    passcode_verification: BlockerPasscodeVerification,
    passkey_upsell_blocker: BlockerPasskeysUpsell,
    password_creation_blocker: BlockerPasswordCreation,
    password_verification_blocker: BlockerPasswordVerification,
    pay_with_cash_authorization_blocker: BlockerPayWithCashAuthorization,
    payment_device_customization_blocker: BlockerPaymentDeviceCustomization,
    phone_number: BlockerPhoneNumber,
    phone_verification: BlockerPhoneVerification,
    reward_code: BlockerRewardCode,
    round_up_onboarding_blocker: RoundUpOnboardingBlocker,
    selection: BlockerSelection,
    set_paycheck_allocation_amount_blocker: BlockerSetAllocationAmount,
    signature: BlockerSignature,
    status_interstitial_blocker: BlockerStatusInterstitial,
    tag_confirmation_blocker: BlockerTagConfirmation,
    tax_web_view_blocker: BlockerTaxWebViewBlocker,
    transfer_option_selection_blocker: BlockerTransferOptionSelection,
  }
  /* eslint sort-keys: "off" */

  return blockerComponentRegistry[name]
}

/* eslint sort-keys: "warn" */
const customFormBlockerRegistry: RegisteredCustomBlockerComponents = {
  /**
   * custom components that should technically be rendered by the form blocker.
   * this is achieved by using the id of the form blocker instead of by type
   * we shouldn't do this unless absolutely necessary
   */
  acknowledge_recovery_codes: BlockerTotpRecoveryCodes,
  start_totp_enrollment: BlockerStartTotpEnrollment,
}
/* eslint-disable sort-keys */

export const getRegisteredComponent = (lookup: RegistryLookup, overrides?: BlockerComponentOverrides) => {
  // Look up in overrides first
  if (overrides) {
    // Check custom overrides by type
    if (lookup.type != null && overrides[lookup.type] != null) {
      return overrides[lookup.type]
    }
  }

  // look up by id (form blocker) first
  if (lookup.id != null && customFormBlockerRegistry[lookup.id] != null) {
    return customFormBlockerRegistry[lookup.id]
  }
  // look up by type (default)
  if (lookup.type != null && lookupBlocker(lookup.type) != null) {
    return lookupBlocker(lookup.type)
  }
  // if we didn't find anything return null
  return null
}
